import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Clock, Search, Star, User, Store, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

// Green theme tokens
const THEME = {
  primary: '#86EFAC',
  light: '#DCFCE7',
  lighter: '#F0FDF4',
  dark: '#166534',
  icon: '#22C55E',
  border: '#D1FAE5',
  surface: '#FAFDF7',
};

const HistoryTab = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async (search = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await API.get(`/shopping/history?${params.toString()}`);
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchHistory(searchQuery);
  };

  const handleSaveAsFavorite = async (item) => {
    try {
      await API.post(`/shopping/items/${item.id}/to-favorite`);
      toast.success('Saved to favorites!');
    } catch (error) {
      toast.error('Failed to save as favorite');
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/shopping/items/${id}`);
      toast.success('Removed from history');
      fetchHistory(searchQuery);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <>
      {/* Section Header */}
      <div className="mb-8">
        <h3 
          className="text-xl font-semibold"
          style={{ color: THEME.dark }}
        >
          Shopping History
        </h3>
        <p className="text-sm text-gray-500 mt-1">Purchased and removed items</p>
      </div>

      {/* Search */}
      <div className="flex gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search history..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-11 py-5 rounded-xl border-gray-200 focus:border-green-300 focus:ring-green-200"
          />
        </div>
        <Button 
          onClick={handleSearch} 
          variant="outline"
          className="px-5 rounded-xl hover:bg-green-50"
          style={{ borderColor: THEME.border }}
        >
          Search
        </Button>
      </div>

      {history.length === 0 ? (
        <div 
          className="text-center py-16 rounded-2xl border-2 border-dashed"
          style={{ 
            backgroundColor: THEME.lighter,
            borderColor: THEME.border,
          }}
        >
          <div 
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: THEME.light }}
          >
            <Clock className="h-8 w-8" style={{ color: THEME.dark }} />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No history yet</h4>
          <p className="text-gray-500 max-w-sm mx-auto">
            Purchased items will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => (
            <Card 
              key={item.id} 
              className="overflow-hidden"
              style={{ 
                backgroundColor: item.status === 'purchased' ? THEME.lighter : '#F9FAFB',
                borderColor: item.status === 'purchased' ? THEME.border : '#E5E7EB',
              }}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span 
                        className="font-medium"
                        style={{ 
                          color: item.status === 'purchased' ? THEME.dark : '#6B7280'
                        }}
                      >
                        {item.name}
                      </span>
                      <Badge 
                        variant="outline" 
                        className="text-xs font-medium"
                        style={{ 
                          borderColor: item.status === 'purchased' 
                            ? THEME.border 
                            : '#E5E7EB',
                          color: item.status === 'purchased' 
                            ? THEME.dark 
                            : '#6B7280',
                          backgroundColor: item.status === 'purchased'
                            ? THEME.light
                            : 'transparent',
                        }}
                      >
                        {item.status}
                      </Badge>
                    </div>
                    
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      {item.purchased_at && (
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          {formatDate(item.purchased_at)}
                        </span>
                      )}
                      {item.linked_person && (
                        <span className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5" />
                          {item.linked_person}
                        </span>
                      )}
                      {item.store && (
                        <span className="flex items-center gap-1.5">
                          <Store className="h-3.5 w-3.5" />
                          {item.store}
                        </span>
                      )}
                    </div>
                    
                    {item.why_buying && (
                      <p className="text-xs text-gray-500 mt-2 italic">
                        {item.why_buying}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-1 flex-shrink-0 ml-3">
                    {!item.favorite_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSaveAsFavorite(item)}
                        className="text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                        title="Save as favorite"
                      >
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item.id)}
                      className="text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

export default HistoryTab;
